import { useLocationHash, useLocationPathname, useLocationSearch, useLocationState, useNavigate } from '@rocketlane/rocket-router';
import { useCallback, useMemo } from 'react';
import { useHashRevision } from 'hooks/use-hash-revision';
const PPI_IN_ROUTE_REGEX = /#ppi=(\d+)/;
export const useCustomerPortalHashMap = ()=>{
    // The customer portal addresses its task pane through `hashMap.task_drawer`,
    // so it is exposed to the same missed-notification bug as the internal drawer:
    // `useLocationHash` does not always notify on a hash-only change. This only
    // forces the render; `useLocationHash` below is still the value.
    useHashRevision();
    const hash = useLocationHash();
    const pathname = useLocationPathname();
    const search = useLocationSearch();
    const state = useLocationState();
    const navigate = useNavigate();
    const hashMap = useMemo(()=>{
        const split = hash.replace('#', '').split('&');
        const parsed = split.reduce((acc, curr)=>{
            const [key, value] = curr.split('=');
            if (!key) {
                return acc;
            }
            let decodedValue;
            try {
                decodedValue = decodeURIComponent(value || '');
            } catch (unused) {
                decodedValue = value || '';
            }
            acc[key] = decodedValue;
            // Extract ppi from route value if it contains #ppi=X
            if (key === 'route' && decodedValue.includes('#ppi=')) {
                const ppiMatch = decodedValue.match(PPI_IN_ROUTE_REGEX);
                if (ppiMatch) {
                    const [, ppiValue] = ppiMatch;
                    if (ppiValue) {
                        acc.ppi = ppiValue;
                        // Clean the route value to remove the ppi part
                        acc[key] = decodedValue.replace(PPI_IN_ROUTE_REGEX, '').replace(/#$/, '');
                    }
                }
            }
            return acc;
        }, {});
        return parsed;
    }, [
        hash
    ]);
    const constructHash = useCallback((entries)=>{
        return Object.entries(entries !== null && entries !== void 0 ? entries : hashMap).filter(([key])=>!!key).map(([key, value])=>`${key}=${value}`).join('&');
    }, [
        hashMap
    ]);
    const setHashMap = useCallback((_key, _value)=>{
        hashMap[_key] = _value;
        const newHash = constructHash();
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            replace: true
        });
    }, [
        constructHash,
        hashMap,
        navigate,
        pathname,
        search
    ]);
    const pushToHashMap = useCallback((_key, _value)=>{
        hashMap[_key] = _value;
        const newHash = constructHash();
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            replace: true
        });
    }, [
        constructHash,
        hashMap,
        navigate,
        pathname,
        search
    ]);
    const batchUpdate = useCallback((updates, navOptions)=>{
        var _ref;
        Object.entries(updates).forEach(([key, value])=>{
            hashMap[key] = value;
        });
        const newHash = constructHash();
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            replace: (_ref = navOptions === null || navOptions === void 0 ? void 0 : navOptions.replace) !== null && _ref !== void 0 ? _ref : true,
            state: navOptions === null || navOptions === void 0 ? void 0 : navOptions.state
        });
    }, [
        constructHash,
        hashMap,
        navigate,
        pathname,
        search
    ]);
    const removeKey = useCallback((_key)=>{
        delete hashMap[_key];
        const newHash = constructHash();
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            replace: true
        });
    }, [
        constructHash,
        hashMap,
        navigate,
        pathname,
        search
    ]);
    const removeKeys = useCallback((_keys)=>{
        _keys.forEach((key)=>{
            delete hashMap[key];
        });
        const newHash = constructHash();
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            state
        });
    }, [
        constructHash,
        hashMap,
        navigate,
        pathname,
        search,
        state
    ]);
    const overwriteHashMap = useCallback((newHashMap)=>{
        const newHash = constructHash(newHashMap);
        navigate({
            pathname,
            hash: newHash,
            search
        }, {
            replace: true,
            state
        });
    }, [
        constructHash,
        navigate,
        pathname,
        search,
        state
    ]);
    return {
        hashMap,
        setHashMap,
        removeKey,
        pushToHashMap,
        batchUpdate,
        overwriteHashMap,
        removeKeys
    };
};
